import React, { FunctionComponent, h } from "preact"; // eslint-disable-line @typescript-eslint/no-unused-vars
import { useEffect, useState } from "preact/hooks";

import cx from "classnames";

import Pagination from "../../components/Pagination";
import ResultItem, { IResult } from "../components/Result";
import utils from "../../../helpers/utils";
import NoResults from "../components/NoResults";
import { ALL, searchDefaults } from "../../../helpers/constants";
import Loading from "../../components/Loading";

export interface ISearchTabProps {
  data: any;
  loading: boolean;
  params: any;
  currentPage?: number;
  settings: any;
  handleChangeSuperCategory: () => void;
  handleChangePage: (page: number) => void;
  handleSort: (e) => void;
  sortField: string;
}

const SearchTabs: FunctionComponent<ISearchTabProps> = ({
  data,
  loading,
  params,
  settings,
  currentPage,
  children,
  handleChangeSuperCategory,
  handleChangePage,
  handleSort,
  sortField,
}) => {
  const renderLoading = () => {
    return loading ? (
      <div
        className="search-results"
        style={{ width: "100%" }}
      >
        <Loading text={settings && settings.loadingText} />
      </div>
    ) : null;
  };

  if (!data) {
    return renderLoading();
  }

  const { items, superCategories, totalHits } = data;
  const superCategoryGroups = superCategories.order;

  if (!superCategoryGroups) {
    return null;
  }

  const [groupedResults, setGroupedResults] = useState<any>(null);
  const [activeTabId, setActiveTabId] = useState<string | null>(ALL);
  const [currentPageItems, setCurrentPageItems] = useState<any[]>([]);
  const [showFiltersMobile, setShowFiltersMobile] = useState<boolean>(false);
  const handleToggleFilters = () => {
    setShowFiltersMobile(!showFiltersMobile);
  };
  const superCategory = utils.getUrlParameter("superCategory");
  const activeAllTab = activeTabId === ALL || !activeTabId;
  const { noResultsText, standaloneSuperCategory } = settings;
  const pageSize = settings.pageSize || searchDefaults.PAGE_SIZE; // We should always have the fallback on the settings, but double-guarding it anyway
  useEffect(() => {
    setCurrentPageItems(data.items);
  }, [data]);

  useEffect(() => {
    const groupedResults = data.facets[0].facets.map((item) => item.displayName).flat();
    const group = [...new Set(groupedResults)];
    setGroupedResults(group);
  }, [items]);

  useEffect(() => {
    setActiveTabId(superCategory);
  }, [superCategory]);

  const renderResult = (result: IResult, tabId: string) => {
    const { title } = result;
    if (!title) {
      return;
    }
    const id = title.replace(" ", "-");
    return (
      <li
        key={id}
        className="search-results__item"
      >
        <ResultItem
          id={id}
          title={title}
          item={result}
          settings={settings}
          includeRelated={true}
          tabId={tabId}
        ></ResultItem>
      </li>
    );
  };

  const activateTab = (newId?: string) => (e: any) => {
    if (activeTabId != newId || newId == undefined) {
      const tabId = newId || e.target.value;
      let newParams = Object.assign(params, { superCategory: tabId });
      if (tabId === ALL) {
        delete newParams.superCategory; // Get all results upfront to show the superCategories (tabs) correctly - ignore superCategory
      }
      window.history.pushState(newParams, "Current tab state", "?" + utils.serializeParams(newParams));
      setActiveTabId(tabId);
      handleChangeSuperCategory();
      e.preventDefault();
    }
  };

  const renderPagination = () => {
    return (
      !!totalHits && (
        <Pagination
          page={currentPage}
          pageSize={pageSize}
          totalItems={totalHits}
          handleChangePage={handleChangePage}
          className="search-results__pagination"
        ></Pagination>
      )
    );
  };

  if (!groupedResults) {
    return null;
  }

  return (
    <div className="search-tabs-container">
      <div className="search-mobile">
        <div className="search-mobile__pull-tab-container">
          <button
            className="pull-tab search-mobile__pull-tab unbutton"
            onClick={handleToggleFilters}
          >
            Filters
          </button>
        </div>
        {/* Mobile filters */}
        <div
          className="search-mobile__filters"
          style={{ display: showFiltersMobile ? "block" : "none" }}
        >
          {children}
        </div>
        {/* Mobile version - native dropdown */}
        {!standaloneSuperCategory && (
          <div className="search-mobile__categories">
            <label
              htmlFor="tabs-mobile"
              className="visually-hidden"
            >
              Select:
            </label>
            <select
              id="tabs-mobile"
              className="search-mobile__select"
              onChange={activateTab()}
            >
              <option
                value={ALL}
                href={`#${ALL}`}
                selected={activeTabId === ALL}
              >
                All
              </option>
              {superCategoryGroups.map((superCategory) => {
                const id = superCategory.superCategoryId;
                return (
                  <option
                    value={id}
                    selected={activeTabId === id}
                    href={`#${id}`}
                  >
                    {superCategory.displayName}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>

      {/* This can be passed into a more generic <Tabs> component */}
      {/* Add accessibility to tabs */}
      {/* If user lands on an non-existent tab - load "All" */}
      <div className="tabs search-tabs">
        <ul className="tabs__list search-tabs__list">
          {/* ALL tab doesn't show on standalone superCategory */}
          {!standaloneSuperCategory && (
            <li
              className={cx("tabs__item search-tabs__item", {
                "tabs__active search-tabs__active": activeAllTab,
              })}
            >
              <a
                className="tabs__action search-tabs__action"
                href={`#${ALL}`}
                onClick={
                  !activeAllTab
                    ? activateTab(ALL)
                    : (e) => {
                        e.preventDefault();
                      }
                }
              >
                All
              </a>
            </li>
          )}
          {superCategoryGroups.map((superCategory) => {
            const id = superCategory.superCategoryId;
            const currentTab = activeTabId === id || standaloneSuperCategory === id;
            const showTab = !standaloneSuperCategory || currentTab;
            if (showTab) {
              return (
                <li
                  className={cx("tabs__item search-tabs__item", {
                    "tabs__active search-tabs__active": currentTab,
                    "tabs__empty search-tabs__empty": !groupedResults.includes(id),
                  })}
                >
                  <a
                    className="tabs__action search-tabs__action"
                    href={`#${id}`}
                    onClick={
                      !currentTab
                        ? activateTab(id)
                        : (e) => {
                            e.preventDefault();
                          }
                    }
                  >
                    {superCategory.displayName}
                  </a>
                </li>
              );
            }
          })}
        </ul>
      </div>

      {/* "All" view - also the first tab content - hidden on standalone superCategory */}
      {!standaloneSuperCategory && (
        <div
          className={cx("tabs__content search-tabs__content search-results", {
            "tabs__content--active search-tabs__content--active": activeAllTab,
          })}
        >
          <div className="search-results__top">
            <div className="h4 search-results__total">
              <span className="search-results__total-number">{totalHits || 0}&nbsp;</span>
              Total results
            </div>
            <div className="search-results__sort">
              <label className="search-results__sort-label">Sort by:</label>
              <label
                className="visually-hidden"
                htmlFor="search-results__sort-select-id"
              ></label>
              <select
                id="search-results__sort-select-id"
                className="search-results__sort-select"
                onChange={handleSort}
              >
                <option
                  value="rel"
                  selected={sortField === "rel"}
                >
                  Relevance
                </option>
                <option
                  value="a-z"
                  selected={sortField === "a-z"}
                >
                  From A to Z
                </option>
                <option
                  value="z-a"
                  selected={sortField === "z-a"}
                >
                  From Z to A
                </option>
                <option
                  value="publish"
                  selected={sortField === "publish"}
                >
                  Published date
                </option>
              </select>
            </div>
          </div>
          {!totalHits && (
            <div className="search-results-group">
              <NoResults text={noResultsText} />
            </div>
          )}
          {!!currentPageItems?.length && (
            <div className="search-results-group">
              <ol className="search-results__list">
                {currentPageItems.map((res) => {
                  return renderResult(res, "");
                })}
              </ol>
            </div>
          )}
          {!!currentPageItems?.length && renderPagination()}
        </div>
      )}

      {/* All the other tab contents */}
      {superCategoryGroups.map((superCategory) => {
        const id = superCategory.superCategoryId;
        const currentTab = activeTabId === id || standaloneSuperCategory === id;
        const resultsToShow = currentTab && !!currentPageItems?.length;
        if (loading) {
          return (
            <div
              className={cx("tabs__content search-tabs__content search-results ", {
                "tabs__content--active search-tabs__content--active": currentTab,
              })}
            >
              {renderLoading()}
            </div>
          );
        }
        return (
          <div
            className={cx("tabs__content search-tabs__content search-results ", {
              "tabs__content--active search-tabs__content--active": currentTab,
            })}
          >
            <div className="search-results__top">
              <div className="h4 search-results__total">
                <span className="search-results__total-number">{totalHits || 0}&nbsp;</span>
                Total results
              </div>
              <div className="search-results__sort">
                <label className="search-results__sort-label">Sort by:</label>
                <label
                  className="visually-hidden"
                  htmlFor="search-results__sort-select-id"
                ></label>
                <select
                  className="search-results__sort-select"
                  id="search-results__sort-select-id"
                  onChange={handleSort}
                >
                  <option
                    value="rel"
                    selected={sortField === "rel"}
                  >
                    Relevance
                  </option>
                  <option
                    value="a-z"
                    selected={sortField === "a-z"}
                  >
                    From A-Z
                  </option>
                  <option
                    value="z-a"
                    selected={sortField === "z-a"}
                  >
                    From Z-A
                  </option>
                  <option
                    value="publish"
                    selected={sortField === "publish"}
                  >
                    Published date
                  </option>
                </select>
              </div>
            </div>
            <div className="search-results-group">
              {resultsToShow && (
                <ol>
                  {currentPageItems.map((res) => {
                    return renderResult(res, id);
                  })}
                </ol>
              )}
              {!currentPageItems?.length && <NoResults text={noResultsText} />}
            </div>
            {/* Show the pagination for the current tab */}
            {resultsToShow && renderPagination()}
          </div>
        );
      })}
    </div>
  );
};

export default SearchTabs;
